import { AlertPriority, AlertPriorityOrder } from '@ixicare/resources';
import { t } from '~i18n';
import { SimpleAlert } from '~store/Alert/Alerts.types';

class NotificationService {
  private lastNotification?: Notification;
  private permissionStatus?: PermissionStatus;
  private silenced = false;
  private passiveSilenced = false;
  private silenceListeners = new Set<() => void>();

  constructor() {
    if (typeof window === 'undefined') return;
    if ('permissions' in navigator) {
      navigator.permissions.query({ name: 'notifications' }).then((permissionStatus) => {
        this.permissionStatus = permissionStatus;
      });
    }
  }

  /** Effective state: only true when permission is granted AND not silenced by
   *  presence or by passive (view-only) alert mode. The bell icon reads this to
   *  reflect whether a notification would actually fire. */
  public get isEnabled() {
    return this.permissionStatus?.state === 'granted' && !this.silenced && !this.passiveSilenced;
  }

  /** Raw browser permission state — used by code that wants to know whether
   *  re-enabling would require a permission prompt. */
  public get isPermissionGranted() {
    return this.permissionStatus?.state === 'granted';
  }

  onPermissionsChange(callback: () => void) {
    this.permissionStatus?.addEventListener('change', callback);
    return () => this.permissionStatus?.removeEventListener('change', callback);
  }

  /** Subscribe to silence-flag changes. */
  subscribe(listener: () => void): () => void {
    this.silenceListeners.add(listener);
    return () => this.silenceListeners.delete(listener);
  }

  setSilenced(silenced: boolean) {
    if (this.silenced === silenced) return;
    this.silenced = silenced;
    if (silenced) this.tearDownVisibleNotification();
    this.silenceListeners.forEach((cb) => cb());
  }

  /** Silence notifications while in passive (view-only) alert mode. Independent
   *  of presence silencing so the two compose. */
  setPassiveSilenced(silenced: boolean) {
    if (this.passiveSilenced === silenced) return;
    this.passiveSilenced = silenced;
    if (silenced) this.tearDownVisibleNotification();
    this.silenceListeners.forEach((cb) => cb());
  }

  private tearDownVisibleNotification() {
    // Don't leave a lingering toast once silenced.
    this.lastNotification?.close();
    this.lastNotification = undefined;
  }

  enable() {
    Notification.requestPermission();
  }

  updateAlertCounters(activeAlerts: SimpleAlert[]) {
    if (!this.isEnabled) return;
    this.lastNotification?.close();
    if (activeAlerts.length > 0) {
      let description = t('foregroundNotificaton.state.empty.description');
      description = AlertPriorityOrder.map((prio): [AlertPriority, number] => [
        prio,
        activeAlerts.filter((x) => x.priority === prio).length,
      ])
        .filter(([, count]) => count > 0)
        .map(([priority, count]) => `${count} ${t(`alert.priority.${priority.toLowerCase()}`)}`)
        .join(', ');
      const title = t(
        activeAlerts.length ? 'foregroundNotificaton.state.active.title' : 'foregroundNotificaton.state.empty.title',
        { count: activeAlerts.length }
      );
      this.lastNotification = new Notification(title, { body: description });
    }
  }

  disconnected() {
    if (!this.isEnabled) return;
    this.lastNotification?.close();
    this.lastNotification = new Notification(t('foregroundNotificaton.state.disconnected.title'), {
      body: t('foregroundNotificaton.state.disconnected.description'),
    });
  }
}

export default new NotificationService();
